import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';
import { getPopular } from './PopularItems';
import { tours } from '../constants';

/**
 * Custom hook for filtering, sorting, and paginating tours.
 */
export const useTourFilter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { search } = useParams();
  const [searchParams] = useSearchParams();

  // State management
  const [matchingTours, setMatchingTours] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  // Set items per page based on route
  const itemsPerPage = location.pathname.toLowerCase().includes('list') ? 4 : 6;

  // Update search keyword from URL param
  useEffect(() => {
    setSearchKeyword(search || '');
  }, [search]);

  // Filtering and sorting logic triggered by URL changes or user actions
  useEffect(() => {
    const destination = searchParams.get('destination') || '';
    const departureDate = searchParams.get('departureDate') || '';
    const duration = searchParams.get('duration') || '';
    const tourType = searchParams.get('tourType') || '';
    const activity = searchParams.get('activity') || '';
    const minPrice = searchParams.get('minPrice') || '';
    const maxPrice = searchParams.get('maxPrice') || '';

    // Apply form-based filtering
    let filteredTours = handleSearch(
      destination,
      departureDate,
      duration,
      tourType,
      activity,
      minPrice,
      maxPrice,
      tours
    );

    // Apply keyword search (if present)
    if (searchKeyword) {
      filteredTours = handleHeaderSearch(searchKeyword);
    }

    // Apply sorting
    filteredTours = handleSortTours(filteredTours, sortOption);

    // Update matching tours
    setMatchingTours(filteredTours);
    setCurrentPage(1);  
  }, [searchParams, searchKeyword, sortOption]);

  // Sort tours based on selected option
  const handleSortTours = (tours, sortOption) => {
    switch (sortOption) {
      case 'featured': return getPopular(tours);
      case 'latest': return [...tours].sort((a, b) => new Date(b.details.checkIn) - new Date(a.details.checkIn));
      case 'lowPrice': return [...tours].sort((a, b) => a.details.price - b.details.price);
      case 'highPrice': return [...tours].sort((a, b) => b.details.price - a.details.price);
      case 'shortDuration': return [...tours].sort((a, b) => {
        const aDays = parseInt(a.details.duration);
        const bDays = parseInt(b.details.duration);
        return aDays - bDays;
      });
      case 'longDuration': return [...tours].sort((a, b) => {
        const aDays = parseInt(a.details.duration);
        const bDays = parseInt(b.details.duration);
        return bDays - aDays;
      });
      default: return tours;
    }
  };

  // Filter tours based on form fields
  const handleSearch = (
    destination,
    departureDate,
    duration,
    tourType,
    activity,
    minPrice,
    maxPrice,
    tours
  ) => {
    return tours.filter((tour) => {
      const parsedMinPrice = minPrice ? Number(minPrice) : 0;
      const parsedMaxPrice = maxPrice ? Number(maxPrice) : Infinity;
      const tourPrice = tour.details.price;
      
      // Convert dates for comparison
      const tourDepartureDate = new Date(tour.details.checkIn);
      const searchDepartureDate = departureDate ? new Date(departureDate) : null;

      // Normalize both dates for comparison (remove time component)
const sameOrAfterDeparture =
  !searchDepartureDate ||
  tourDepartureDate.toDateString() >= searchDepartureDate.toDateString();
      
      // Duration comparison (extract numeric part)
      const tourDuration = parseInt(tour.details.duration) || 0;
      const searchDuration = duration ? parseInt(duration) : null;

          // Activity comparison - split by comma and check if any matches
    const activityMatch = activity === '' || 
      tour.details.activity.toLowerCase().split(',').some(tourActivity => 
        tourActivity.trim().includes(activity.toLowerCase())
      );

      return (
        (destination === '' || tour.details.location.toLowerCase().includes(destination.toLowerCase())) &&
        sameOrAfterDeparture &&
        (!searchDuration || tourDuration === searchDuration) &&
        (tourType === '' || tour.details.tourType.toLowerCase().includes(tourType.toLowerCase())) &&
        activityMatch &&
        tourPrice >= parsedMinPrice &&
        tourPrice <= parsedMaxPrice
      );
    });
  };

  // Search via global keyword (from header)
  const handleHeaderSearch = (searchTerm) => {
    const searchLower = searchTerm.toLowerCase();
    return tours.filter((tour) => (
      tour.title.toLowerCase().includes(searchLower) || 
      tour.details.location.toLowerCase().includes(searchLower) ||
      tour.details.tourType.toLowerCase().includes(searchLower) ||
      tour.details.activity.toLowerCase().includes(searchLower)
    ));
  };

  // Form submission handler: updates URL with selected filters
  const handleFormSubmit = ({
    destination,
    departureDate,
    duration,
    tourType,
    activity,
    minPrice,
    maxPrice
  }) => {
    const params = new URLSearchParams();
    if (destination) params.set('destination', destination);
    if (departureDate) params.set('departureDate', departureDate);
    if (duration) params.set('duration', duration);
    if (tourType) params.set('tourType', tourType);
    if (activity) params.set('activity', activity);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);

    navigate(`/Tours?${params.toString()}`);
    setCurrentPage(1);
  };

  // Header search submit handler: navigates to search page
  const handleHeaderSearchSubmit = (selectedSearchQuery) => {
    const encodedQuery = encodeURIComponent(selectedSearchQuery);
    navigate(`/Tours/search/${encodedQuery}`);
  };

  // Sort option change handler
  const handleSort = (selectedOption) => {
    setSortOption(selectedOption ? selectedOption.value : '');
    setCurrentPage(1);
  };

  // Pagination logic
  const displayTours = matchingTours.length > 0 ? matchingTours : tours;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedTours = displayTours.slice(startIndex, endIndex);
  const totalPages = Math.ceil(displayTours.length / itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return {
    searchKeyword,
    matchingTours,
    sortOption,
    currentPage,
    displayedTours,
    handleFormSubmit,
    handleSort,
    totalPages,
    handlePageChange,
    handleHeaderSearchSubmit
  };
};

// ================================
// Extract Dynamic Counts for Filtering
// ================================

const destinationCounts = {};
const durationCounts = {};
const tourTypeCounts = {};
const activityCounts = {};

// Loop through tour list and extract counts for filter dropdowns
tours.forEach((tour) => {
  const destination = tour.details.location.toLowerCase();
  const duration = tour.details.duration.toLowerCase();
  const tourType = tour.details.tourType.toLowerCase();
  const activity = tour.details.activity.toLowerCase();

  destinationCounts[destination] = (destinationCounts[destination] || 0) + 1;
  durationCounts[duration] = (durationCounts[duration] || 0) + 1;
  tourTypeCounts[tourType] = (tourTypeCounts[tourType] || 0) + 1;
  activityCounts[activity] = (activityCounts[activity] || 0) + 1;
});

// ================================
// Generate Dropdown Options
// ================================

export const destinationOptions = [
  { value: '', label: 'Destination' },
  ...Object.keys(destinationCounts).map((destination) => ({
    value: destination,
    label: destination.charAt(0).toUpperCase() + destination.slice(1),
    count: destinationCounts[destination]
  })),
];

export const durationOptions = [
  { value: '', label: 'Duration' },
  ...Object.keys(durationCounts).map((duration) => ({
    value: duration,
    label: duration,
    count: durationCounts[duration]
  })),
];

export const tourTypeOptions = [
  { value: '', label: 'Tour Type' },
  ...Object.keys(tourTypeCounts).map((type) => ({
    value: type,
    label: type.charAt(0).toUpperCase() + type.slice(1),
    count: tourTypeCounts[type]
  })),
];


// Update the activityOptions generation to split and flatten all activities
export const activityOptions = (() => {
  // Get all unique activities by splitting comma-separated values
  const allActivities = tours.flatMap(tour => 
    tour.details.activity.toLowerCase().split(',').map(a => a.trim())
  );
  
  // Count occurrences of each activity
  const activityCounts = {};
  allActivities.forEach(activity => {
    activityCounts[activity] = (activityCounts[activity] || 0) + 1;
  });

  return [
    { value: '', label: 'Activity' },
    ...Object.keys(activityCounts).map((activity) => ({
      value: activity,
      label: activity.charAt(0).toUpperCase() + activity.slice(1),
      count: activityCounts[activity]
    })),
  ];
})();

// ================================
// Utility Functions
// ================================

// Extract search params from URL
export const extractSearchParams = (searchParams) => {
  return {
    destination: searchParams.get('destination') || '',
    departureDate: searchParams.get('departureDate') || '',
    duration: searchParams.get('duration') || '',
    tourType: searchParams.get('tourType') || '',
    activity: searchParams.get('activity') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || ''
  };
};

// Generic change handler for select inputs
export const handleSelectChange = (setter) => (selectedOption) => {
  setter(selectedOption ? selectedOption.value : '');
};

// Get unique options from tours array
export const getUniqueOptions = (tours, field) => {
  const values = tours.map(tour => tour.details[field] || tour[field]);
  return [...new Set(values)].map(value => ({
    value,
    label: value
  }));
};

// Function to filter tours by location
export const filterToursByLocation = (location) => {
    return tours.filter(tour => tour.details.location === location);
};